import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import {
  provideHttpClient,
  withInterceptors,
  withJsonpSupport,
} from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { environment } from '../environments/environment';
import { routes } from './routes';
import { firebaseTokenInterceptor } from './firebase-token-interceptor.interceptor';

/**
 * What the root injector holds.
 *
 * Lifted out of main.ts so it can be asserted against: a service declared
 * `providedIn: 'root'` can only inject what is listed here, and nothing about
 * that is visible at compile time. ProfileDialogService injecting MatDialog
 * was the first to find out - it threw NullInjectorError while the router was
 * building the events page, so the page rendered as nothing at all, with the
 * real cause only in the browser console. app.config.spec.ts now builds this
 * list for real and asks for the service.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([firebaseTokenInterceptor]),
      withJsonpSupport()
    ),
    importProvidersFrom(
      BrowserModule,
      /*
       * MatDialog is provided by MatDialogModule and is never
       * `providedIn: 'root'`. A standalone component importing MatDialogModule
       * gets it in that component's own injector, which a root-provided
       * service cannot reach - so the one shared dialog opener needs it here.
       */
      MatDialogModule,
      provideFirebaseApp(() => initializeApp(environment.firebase)),
      provideAuth(() => getAuth())
    ),
    provideAnimations(),
  ],
};
